import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RelevanceMatchBadge } from './RelevanceMatchBadge';
import { ShareJobMenu } from './ShareJobMenu';
import { EasyApplyButton } from './EasyApplyButton';
import { buildJobShareUrl } from '@/features/jobs/utils/jobHelpers';
import { useLongPressCopy } from '@/features/jobs/utils/longPressCopy';
import { JOB_SHARE_REF } from '@/features/jobs/utils/jobOrigin';
import { ROUTES } from '@/app/router/routes';
import styles from './JobCardParts.module.css';

/**
 * Building blocks of the job cards in the list/detail layouts — Home / Jobs (HomeJobListItem)
 * and My Jobs (ProfileJobCard). Mirrors the shared pieces of Flutter WebJobDetailsCard and
 * ProfileJobCard: title + hot flame, relevance match, save / share, icon rows (IconRow).
 */

export { styles as jobCardStyles };

/** Buttons inside a card act on their own; the rest of the card selects the job. */
export function stopThen(fn: () => void) {
  return (e: MouseEvent) => {
    e.stopPropagation();
    fn();
  };
}

export function JobCardFrame({
  isSelected,
  onSelect,
  children,
}: {
  isSelected: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <article
      className={`${styles.item} ${isSelected ? styles.selected : ''}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      tabIndex={0}
      aria-current={isSelected || undefined}
    >
      {children}
    </article>
  );
}

/**
 * The job title as a link to the job page. `href` is where the card's own handler goes (shown on
 * hover, and copied / opened by the browser's link menu); a click runs `onOpen`, which logs and
 * opens the page the way that list does (new tab or this tab), not the browser default.
 */
export interface JobTitleLink {
  href: string;
  newTab: boolean;
  onOpen: () => void;
}

/** The job page link most lists use: /jobs/:displayId with the internal-link ref, in a new tab. */
export function internalJobHref(displayId: string | undefined): string {
  return `${ROUTES.JOBS}/${displayId ?? ''}?ref=${JOB_SHARE_REF.INTERNAL}`;
}

interface JobCardHeadProps {
  title?: string;
  displayId?: string;
  jobId?: string;
  isHot?: boolean;
  relevanceLevel?: unknown;
  isSaved?: boolean;
  isSaving?: boolean;
  onSave: () => void;
  onUnsave: () => void;
  /** Long-press copies the title (Flutter WebJobDetailsCard title onLongPress) */
  copyTitleOnLongPress?: boolean;
  /** Makes the title a link to the job page (Flutter onTapTitle) */
  titleLink?: JobTitleLink;
}

/** Title (2 lines) + hot flame | relevance match, save / unsave, share. */
export function JobCardHead({
  title,
  displayId,
  jobId,
  isHot = false,
  relevanceLevel,
  isSaved = false,
  isSaving = false,
  onSave,
  onUnsave,
  copyTitleOnLongPress = false,
  titleLink,
}: JobCardHeadProps) {
  const { t } = useTranslation();
  const jobTitle = title || 'N/A';
  const longPressTitle = useLongPressCopy(jobTitle);

  return (
    <div className={styles.head}>
      <h3 className={styles.title} title={jobTitle} {...(copyTitleOnLongPress ? longPressTitle : {})}>
        {titleLink ? (
          <a
            href={titleLink.href}
            className={`${styles.titleText} ${styles.titleLink}`}
            target={titleLink.newTab ? '_blank' : undefined}
            rel={titleLink.newTab ? 'noopener' : undefined}
            onClick={(e) => {
              // The card around it selects the job; the title opens it
              e.stopPropagation();
              e.preventDefault();
              titleLink.onOpen();
            }}
          >
            {jobTitle}
          </a>
        ) : (
          <span className={styles.titleText}>{jobTitle}</span>
        )}
        {isHot && (
          <span className={styles.hotIcon} title={t('Hot Jobs')} aria-label={t('Hot Jobs')}>
            <svg width="12" height="14" viewBox="0 0 14 16" fill="currentColor" aria-hidden="true">
              <path d="M7 0s1 2.6-1 5.2C4.3 7.4 2 8.6 2 11.3 2 14 4.2 16 7 16s5-2 5-4.7c0-2.2-1.2-3.5-2-4.3.1 1.4-.6 2.5-1.6 2.5C9.6 7 9.5 3.2 7 0Z" />
            </svg>
          </span>
        )}
      </h3>

      <div className={styles.headActions}>
        {relevanceLevel !== undefined && <RelevanceMatchBadge level={relevanceLevel} variant="soft" />}
        <button
          type="button"
          className={`${styles.iconBtn} ${isSaved ? styles.saved : ''}`}
          onClick={stopThen(isSaved ? onUnsave : onSave)}
          disabled={isSaving}
          aria-label={isSaved ? t('Unsave job') : t('Save job')}
          title={isSaved ? t('Unsave job') : t('Save job')}
        >
          {isSaving ? (
            <span className={styles.spinner} />
          ) : (
            <svg width="16" height="16" viewBox="0 0 20 20" fill={isSaved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5">
              <path d="M5 3h10a1 1 0 0 1 1 1v13.5l-6-3-6 3V4a1 1 0 0 1 1-1Z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
        <ShareJobMenu
          shareUrl={buildJobShareUrl(displayId ?? 'N/A')}
          jobId={jobId ?? ''}
          jobTitle={jobTitle}
        />
      </div>
    </div>
  );
}

export function JobFacts({ children }: { children: ReactNode }) {
  return <ul className={styles.facts}>{children}</ul>;
}

/**
 * One icon + text fact — Flutter IconRow (icon tooltip = label, text tooltip = full value).
 * Facts flow inline and wrap; `wide` gives a long value (skills) a line of its own.
 */
export function JobFact({
  icon,
  label,
  tooltip,
  wide = false,
  children,
}: {
  icon: ReactNode;
  label: string;
  tooltip?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <li className={`${styles.fact} ${wide ? styles.factWide : ''}`}>
      <span className={styles.factIcon} title={label}>
        {icon}
      </span>
      <span className={styles.factText} title={tooltip ?? label}>
        {children}
      </span>
    </li>
  );
}

/**
 * Easy Apply / Apply / Applied — Flutter WebJobDetailsCard (~323-367) and SimiliarJobCard:
 * Easy Apply (tenant config enableEasyApply) and Apply while not applied, a disabled "Applied"
 * once applied. Clicks don't reach the card.
 */
export function JobApplyActions({
  isApplied,
  enableEasyApply,
  onApply,
}: {
  isApplied: boolean;
  enableEasyApply: boolean;
  onApply: (isEasyApply: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      {enableEasyApply && !isApplied && <EasyApplyButton onClick={stopThen(() => onApply(true))} />}
      {isApplied ? (
        <button
          type="button"
          className={`${styles.applyBtn} ${styles.appliedBtn}`}
          disabled
          onClick={(e) => e.stopPropagation()}
        >
          {t('Applied')}
        </button>
      ) : (
        <button type="button" className={styles.applyBtn} onClick={stopThen(() => onApply(false))}>
          {t('Apply')}
        </button>
      )}
    </>
  );
}

/** Divider row: `start` on the left, actions on the right. */
export function JobCardFooter({ start, children }: { start?: ReactNode; children?: ReactNode }) {
  return (
    <div className={styles.footer}>
      <div className={styles.footerStart}>{start}</div>
      <div className={styles.actions}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Icons — thin-stroke equivalents of the Flutter job card SVGs
// ---------------------------------------------------------------------------

const ICON_PROPS = {
  width: 16,
  height: 16,
  viewBox: '0 0 20 20',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

export function FlagIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M4.5 17.5v-14" />
      <path d="M4.5 4h10l-2.2 3.5 2.2 3.5h-10" />
    </svg>
  );
}

export function LocationIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M10 17s5.5-5 5.5-9a5.5 5.5 0 0 0-11 0c0 4 5.5 9 5.5 9Z" />
      <circle cx="10" cy="8" r="2" />
    </svg>
  );
}

export function SkillsIcon() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="10" cy="10" r="5.2" />
      <circle cx="10" cy="10" r="2" />
      <path d="M10 2.5v2.3M10 15.2v2.3M2.5 10h2.3M15.2 10h2.3M4.7 4.7l1.6 1.6M13.7 13.7l1.6 1.6M4.7 15.3l1.6-1.6M13.7 6.3l1.6-1.6" />
    </svg>
  );
}

export function ExperienceIcon() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="2.5" y="6" width="15" height="10.5" rx="2" />
      <path d="M7 6V4.5A1.5 1.5 0 0 1 8.5 3h3A1.5 1.5 0 0 1 13 4.5V6M2.5 10.5h15" />
    </svg>
  );
}

export function CalendarIcon() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="4" width="14" height="13" rx="2" />
      <path d="M3 8h14M7 2.5v3M13 2.5v3" />
    </svg>
  );
}

export function ClockIcon() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6v4l2.5 1.5" />
    </svg>
  );
}
